import { resolveRunnerIoConfig } from '../../runner/lib/io_config';
import { isSnapshotFileName, parseApple2SnapshotText } from '../../apple2/lib/snapshot';
import { createRiscvSourceMap } from '../../riscv/lib/riscv_srcmap';

const DEFAULT_APPLE2_WATCHES = ['pc_debug', 'a_debug', 'x_debug', 'y_debug', 'opcode_debug', 'speaker'];
const U16_MASK = 0xFFFF;
const RISCV_PHYSTOP_IMM20_ORIGINAL = 0x88000;
const RISCV_PHYSTOP_IMM20_MODERATE = 0x84000;
const RISCV_PHYSTOP_IMM20_AGGRESSIVE = 0x80200;
const DEFAULT_TRANSFER_CHUNK_BYTES = 128 * 1024;

function didCallSucceed(result: Unsafe) {
  return result !== false;
}

async function transferBytesChunked({
  bytes,
  offset = 0,
  chunkBytes = DEFAULT_TRANSFER_CHUNK_BYTES,
  writeChunk,
  yieldControl = null
}: Unsafe = {}) {
  if (!(bytes instanceof Uint8Array) || bytes.length === 0 || typeof writeChunk !== 'function') {
    return false;
  }
  const chunkSize = Math.max(1, Number.parseInt(chunkBytes, 10) || DEFAULT_TRANSFER_CHUNK_BYTES);
  const baseOffset = Number(offset) >>> 0;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const end = Math.min(bytes.length, index + chunkSize);
    const chunk = bytes.subarray(index, end);
    const ok = didCallSucceed(writeChunk(chunk, (baseOffset + index) >>> 0));
    if (!ok) {
      return false;
    }
    if (end < bytes.length && typeof yieldControl === 'function') {
      await yieldControl();
    }
  }
  return true;
}

async function loadBytesIntoMainMemory({
  runtime,
  bytes,
  offset = 0,
  isRom = false,
  supportsRunnerApi = false,
  supportsGenericMemoryApi = false,
  transferChunkBytes = DEFAULT_TRANSFER_CHUNK_BYTES,
  yieldControl = null
}: Unsafe = {}) {
  if (supportsRunnerApi && typeof runtime?.sim?.runner_load_memory === 'function') {
    return transferBytesChunked({
      bytes,
      offset,
      chunkBytes: transferChunkBytes,
      yieldControl,
      writeChunk: (chunk: Unsafe, chunkOffset: Unsafe) => runtime.sim.runner_load_memory(chunk, chunkOffset, { isRom })
    });
  }
  if (supportsGenericMemoryApi && typeof runtime?.sim?.memory_load === 'function') {
    return transferBytesChunked({
      bytes,
      offset,
      chunkBytes: transferChunkBytes,
      yieldControl,
      writeChunk: (chunk: Unsafe, chunkOffset: Unsafe) => runtime.sim.memory_load(chunk, chunkOffset, { isRom })
    });
  }
  return false;
}

async function loadBytesIntoRom({
  runtime,
  bytes,
  offset = 0,
  supportsRunnerApi = false,
  supportsGenericMemoryApi = false,
  transferChunkBytes = DEFAULT_TRANSFER_CHUNK_BYTES,
  yieldControl = null
}: Unsafe = {}) {
  if (supportsRunnerApi && typeof runtime?.sim?.runner_load_rom === 'function') {
    const loadedViaRunnerRom = await transferBytesChunked({
      bytes,
      offset,
      chunkBytes: transferChunkBytes,
      yieldControl,
      writeChunk: (chunk: Unsafe, chunkOffset: Unsafe) => runtime.sim.runner_load_rom(chunk, chunkOffset)
    });
    if (loadedViaRunnerRom) {
      return true;
    }
  }
  return loadBytesIntoMainMemory({
    runtime,
    bytes,
    offset,
    isRom: true,
    supportsRunnerApi,
    supportsGenericMemoryApi,
    transferChunkBytes,
    yieldControl
  });
}

async function loadBytesIntoBootRom({
  runtime,
  bytes,
  supportsRunnerApi = false
}: Unsafe = {}) {
  if (supportsRunnerApi && typeof runtime?.sim?.runner_load_boot_rom === 'function') {
    return didCallSucceed(runtime.sim.runner_load_boot_rom(bytes));
  }
  return false;
}

async function loadBytesIntoDisk({
  runtime,
  bytes,
  offset = 0,
  supportsRunnerApi = false,
  transferChunkBytes = DEFAULT_TRANSFER_CHUNK_BYTES,
  yieldControl = null
}: Unsafe = {}) {
  if (supportsRunnerApi && typeof runtime?.sim?.runner_riscv_load_disk === 'function') {
    return transferBytesChunked({
      bytes,
      offset,
      chunkBytes: transferChunkBytes,
      yieldControl,
      writeChunk: (chunk: Unsafe, chunkOffset: Unsafe) => runtime.sim.runner_riscv_load_disk(chunk, chunkOffset)
    });
  }
  return false;
}

function parseNonNegativeInt(value: Unsafe, fallback = 0) {
  const parsed = Number.parseInt(String(value ?? ''), 0);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback;
  }
  return parsed >>> 0;
}

function parseOptionalPc(value: Unsafe) {
  if (value == null || value === '') {
    return null;
  }
  const parsed = Number.parseInt(String(value), 0);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed >>> 0;
}

function supportsRunnerApiForRuntime(runtime: Unsafe = {}) {
  const sim = runtime?.sim;
  if (!sim) {
    return false;
  }
  if (sim?.features?.hasRunnerApi === true) {
    return true;
  }
  if (typeof sim.runnerCaps === 'function') {
    try {
      return !!sim.runnerCaps();
    } catch {
      // fall through
    }
  }
  if (typeof sim.runner_mode === 'function') {
    try {
      return sim.runner_mode() === true;
    } catch {
      return false;
    }
  }
  return false;
}

function normalizeDefaultBinSpace(value: Unsafe) {
  const token = String(value || 'main').trim().toLowerCase();
  if (token === 'boot' || token === 'bootrom' || token === 'boot_rom') {
    return 'boot_rom';
  }
  if (token === 'rom') {
    return 'rom';
  }
  return 'main';
}

function normalizeDefaultAssetKind(value: Unsafe) {
  const token = String(value || 'main').trim().toLowerCase();
  if (token === 'disk') {
    return 'disk';
  }
  return 'main';
}

function normalizeRiscvFastBootMode(value: Unsafe) {
  if (value === false || value == null) {
    return null;
  }
  if (value === true) {
    return 'moderate';
  }

  const token = String(value).trim().toLowerCase();
  if (token === 'moderate' || token === 'safe') {
    return 'moderate';
  }
  if (token === 'aggressive') {
    return 'aggressive';
  }
  return null;
}

function riscvFastBootPhystopImm20(mode: Unsafe) {
  if (mode === 'aggressive') {
    return RISCV_PHYSTOP_IMM20_AGGRESSIVE;
  }
  if (mode === 'moderate') {
    return RISCV_PHYSTOP_IMM20_MODERATE;
  }
  return null;
}

function resolveDefaultBinConfig(preset: Unsafe = {}) {
  const raw = preset?.defaultBin;
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const path = String(raw.path || '').trim();
  if (!path) {
    return null;
  }

  return {
    path,
    offset: parseNonNegativeInt(raw.offset, 0),
    space: normalizeDefaultBinSpace(raw.space),
    startPc: parseOptionalPc(raw.startPc),
    resetAfterLoad: raw.resetAfterLoad !== false,
    fastBootMode: normalizeRiscvFastBootMode(raw.fastBoot)
  };
}

function resolveDefaultDiskConfig(preset: Unsafe = {}) {
  const raw = preset?.defaultDisk;
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const path = String(raw.path || '').trim();
  if (!path) {
    return null;
  }

  return {
    path,
    offset: parseNonNegativeInt(raw.offset, 0),
    resetAfterLoad: raw.resetAfterLoad === true
  };
}

function resolveDefaultAssetsConfig(preset: Unsafe = {}) {
  const raw = preset?.defaultAssets;
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }

  const out = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }

    const path = String(entry.path || '').trim();
    if (!path) {
      continue;
    }

    out.push({
      kind: normalizeDefaultAssetKind(entry.kind),
      path,
      offset: parseNonNegativeInt(entry.offset, 0),
      space: normalizeDefaultBinSpace(entry.space),
      startPc: parseOptionalPc(entry.startPc),
      resetAfterLoad: entry.resetAfterLoad === true
    });
  }

  return out;
}

function applyRiscvFastBootPhystopPatch(bytes: Unsafe, targetImm20: Unsafe) {
  if (!(bytes instanceof Uint8Array) || bytes.length < 4) {
    return 0;
  }
  const imm20Target = Number(targetImm20) >>> 0;
  if (imm20Target === 0) {
    return 0;
  }
  let patches = 0;
  for (let offset = 0; offset + 4 <= bytes.length; offset += 4) {
    const word = (
      (bytes[offset] & 0xFF)
      | ((bytes[offset + 1] & 0xFF) << 8)
      | ((bytes[offset + 2] & 0xFF) << 16)
      | ((bytes[offset + 3] & 0xFF) << 24)
    ) >>> 0;
    const opcode = word & 0x7F;
    if (opcode !== 0x37) {
      continue; // LUI only
    }
    const imm20 = (word >>> 12) & 0xFFFFF;
    if (imm20 !== RISCV_PHYSTOP_IMM20_ORIGINAL) {
      continue; // original PHYSTOP upper immediate (0x8800_0000)
    }
    const rd = (word >>> 7) & 0x1F;
    const patchedWord = (((imm20Target << 12) >>> 0) | (rd << 7) | 0x37) >>> 0;
    bytes[offset] = patchedWord & 0xFF;
    bytes[offset + 1] = (patchedWord >>> 8) & 0xFF;
    bytes[offset + 2] = (patchedWord >>> 16) & 0xFF;
    bytes[offset + 3] = (patchedWord >>> 24) & 0xFF;
    patches += 1;
  }
  return patches;
}

async function loadRunnerRomAsset({
  runtime,
  state,
  ioConfig,
  supportsRunnerApi,
  supportsGenericMemoryApi,
  transferChunkBytes = DEFAULT_TRANSFER_CHUNK_BYTES,
  yieldControl = null,
  fetchImpl,
  log
}: Unsafe = {}) {
  if (!ioConfig?.rom?.path) {
    return false;
  }

  try {
    const romResp = await fetchImpl(ioConfig.rom.path);
    if (!romResp.ok) {
      log(`Runner ROM load skipped (${romResp.status})`);
      return false;
    }

    const romBytes = new Uint8Array(await romResp.arrayBuffer());
    state.apple2.baseRomBytes = new Uint8Array(romBytes);
    const ok = await loadBytesIntoRom({
      runtime,
      bytes: romBytes,
      offset: ioConfig.rom.offset || 0,
      supportsRunnerApi,
      supportsGenericMemoryApi,
      transferChunkBytes,
      yieldControl
    });
    if (ok) {
      log(`Loaded runner ROM via runner_load_rom: ${ioConfig.rom.path}`);
    } else {
      log(`Runner ROM load failed: ${ioConfig.rom.path}`);
    }
    return ok;
  } catch (err) {
    log(`Failed to load runner ROM: ${err instanceof Error ? err.message : String(err)}`);
  }

  return false;
}

async function loadDefaultBinAsset({
  runtime,
  defaultBin,
  supportsRunnerApi,
  supportsGenericMemoryApi,
  transferChunkBytes = DEFAULT_TRANSFER_CHUNK_BYTES,
  yieldControl = null,
  fetchImpl,
  log
}: Unsafe = {}) {
  if (!defaultBin?.path) {
    return false;
  }

  try {
    const binResp = await fetchImpl(defaultBin.path);
    if (!binResp.ok) {
      log(`Default bin load skipped (${binResp.status}): ${defaultBin.path}`);
      return false;
    }

    let bytes = null;
    let loadOffset = defaultBin.offset;
    let startPc = defaultBin.startPc;

    if (isSnapshotFileName(defaultBin.path)) {
      const snapshot = parseApple2SnapshotText(await binResp.text());
      if (!snapshot) {
        log(`Default snapshot parse failed: ${defaultBin.path}`);
        return false;
      }
      bytes = snapshot.bytes;
      if (Number.isFinite(snapshot.offset)) {
        loadOffset = Math.max(0, Number(snapshot.offset) || 0);
      }
      if (snapshot.startPc != null) {
        const parsedStart = Number(snapshot.startPc) >>> 0;
        const runnerKind = typeof runtime?.sim?.runner_kind === 'function'
          ? runtime.sim.runner_kind()
          : null;
        startPc = (runnerKind === 'mos6502' || runnerKind === 'apple2')
          ? (parsedStart & U16_MASK)
          : parsedStart;
      }
    } else {
      bytes = new Uint8Array(await binResp.arrayBuffer());
    }

    if (defaultBin.fastBootMode && defaultBin.space === 'main') {
      const runnerKind = typeof runtime?.sim?.runner_kind === 'function'
        ? runtime.sim.runner_kind()
        : null;
      if (runnerKind === 'riscv') {
        const phystopImm20 = riscvFastBootPhystopImm20(defaultBin.fastBootMode);
        const patches = applyRiscvFastBootPhystopPatch(bytes, phystopImm20);
        if (patches > 0) {
          const phystop = ((phystopImm20! << 12) >>> 0).toString(16).toUpperCase();
          log(`Applied RISC-V ${defaultBin.fastBootMode} fast-boot PHYSTOP patch (${patches} instruction${patches === 1 ? '' : 's'}, PHYSTOP=0x${phystop})`);
        }
      }
    }

    let loaded = false;

    if (defaultBin.space === 'boot_rom') {
      loaded = await loadBytesIntoBootRom({
        runtime,
        bytes,
        supportsRunnerApi
      });
    } else if (defaultBin.space === 'rom') {
      loaded = await loadBytesIntoRom({
        runtime,
        bytes,
        offset: loadOffset,
        supportsRunnerApi,
        supportsGenericMemoryApi,
        transferChunkBytes,
        yieldControl
      });
    } else {
      loaded = await loadBytesIntoMainMemory({
        runtime,
        bytes,
        offset: loadOffset,
        isRom: false,
        supportsRunnerApi,
        supportsGenericMemoryApi,
        transferChunkBytes,
        yieldControl
      });
    }

    if (!loaded) {
      log(`Default bin load failed or unsupported for space "${defaultBin.space}": ${defaultBin.path}`);
      return false;
    }

    if (startPc != null) {
      if (typeof runtime.sim.runner_set_reset_vector === 'function') {
        const pcApplied = didCallSucceed(runtime.sim.runner_set_reset_vector(startPc));
        if (!pcApplied) {
          const width = startPc > 0xFFFF ? 8 : 4;
          log(`Default bin reset vector apply failed (PC=$${startPc.toString(16).padStart(width, '0')})`);
        }
      } else {
        const width = startPc > 0xFFFF ? 8 : 4;
        log(`Default bin reset vector unsupported (PC=$${startPc.toString(16).padStart(width, '0')})`);
      }
    }

    if (defaultBin.resetAfterLoad && typeof runtime.sim.reset === 'function') {
      runtime.sim.reset();
      await bootstrapMos6502Runner(runtime, log);
    }

    log(`Loaded default bin (${defaultBin.space}) @ 0x${loadOffset.toString(16)}: ${defaultBin.path}`);
    return true;
  } catch (err) {
    log(`Failed to load default bin: ${err instanceof Error ? err.message : String(err)}`);
    return false;
  }
}

async function loadDefaultDiskAsset({
  runtime,
  defaultDisk,
  supportsRunnerApi,
  transferChunkBytes = DEFAULT_TRANSFER_CHUNK_BYTES,
  yieldControl = null,
  fetchImpl,
  log
}: Unsafe = {}) {
  if (!defaultDisk?.path) {
    return false;
  }

  try {
    const diskResp = await fetchImpl(defaultDisk.path);
    if (!diskResp.ok) {
      log(`Default disk load skipped (${diskResp.status}): ${defaultDisk.path}`);
      return false;
    }

    const bytes = new Uint8Array(await diskResp.arrayBuffer());
    const loaded = await loadBytesIntoDisk({
      runtime,
      bytes,
      offset: defaultDisk.offset,
      supportsRunnerApi,
      transferChunkBytes,
      yieldControl
    });
    if (!loaded) {
      log(`Default disk load failed or unsupported: ${defaultDisk.path}`);
      return false;
    }

    if (defaultDisk.resetAfterLoad && typeof runtime.sim.reset === 'function') {
      runtime.sim.reset();
    }

    log(`Loaded default disk @ 0x${defaultDisk.offset.toString(16)}: ${defaultDisk.path}`);
    return true;
  } catch (err) {
    log(`Failed to load default disk: ${err instanceof Error ? err.message : String(err)}`);
    return false;
  }
}

async function loadDefaultAssets({
  runtime,
  defaultAssets,
  supportsRunnerApi,
  supportsGenericMemoryApi,
  transferChunkBytes = DEFAULT_TRANSFER_CHUNK_BYTES,
  yieldControl = null,
  fetchImpl,
  log
}: Unsafe = {}) {
  if (!Array.isArray(defaultAssets) || defaultAssets.length === 0) {
    return false;
  }

  let anyLoaded = false;
  for (const asset of defaultAssets) {
    try {
      const resp = await fetchImpl(asset.path);
      if (!resp.ok) {
        log(`Default asset load skipped (${resp.status}): ${asset.path}`);
        continue;
      }

      const bytes = new Uint8Array(await resp.arrayBuffer());
      let loaded = false;
      if (asset.kind === 'disk') {
        loaded = await loadBytesIntoDisk({
          runtime,
          bytes,
          offset: asset.offset,
          supportsRunnerApi,
          transferChunkBytes,
          yieldControl
        });
      } else if (asset.space === 'boot_rom') {
        loaded = await loadBytesIntoBootRom({
          runtime,
          bytes,
          supportsRunnerApi
        });
      } else if (asset.space === 'rom') {
        loaded = await loadBytesIntoRom({
          runtime,
          bytes,
          offset: asset.offset,
          supportsRunnerApi,
          supportsGenericMemoryApi,
          transferChunkBytes,
          yieldControl
        });
      } else {
        loaded = await loadBytesIntoMainMemory({
          runtime,
          bytes,
          offset: asset.offset,
          isRom: false,
          supportsRunnerApi,
          supportsGenericMemoryApi,
          transferChunkBytes,
          yieldControl
        });
      }

      if (!loaded) {
        log(`Default asset load failed or unsupported (${asset.kind}): ${asset.path}`);
        continue;
      }

      if (asset.startPc != null) {
        if (typeof runtime.sim.runner_set_reset_vector === 'function') {
          const pcApplied = didCallSucceed(runtime.sim.runner_set_reset_vector(asset.startPc));
          if (!pcApplied) {
            const width = asset.startPc > 0xFFFF ? 8 : 4;
            log(`Default asset reset vector apply failed (PC=$${asset.startPc.toString(16).padStart(width, '0')})`);
          }
        } else {
          const width = asset.startPc > 0xFFFF ? 8 : 4;
          log(`Default asset reset vector unsupported (PC=$${asset.startPc.toString(16).padStart(width, '0')})`);
        }
      }

      if (asset.resetAfterLoad && typeof runtime.sim.reset === 'function') {
        runtime.sim.reset();
        await bootstrapMos6502Runner(runtime, log);
      }

      log(`Loaded default asset (${asset.kind}) @ 0x${asset.offset.toString(16)}: ${asset.path}`);
      anyLoaded = true;
    } catch (err) {
      log(`Failed to load default asset: ${err instanceof Error ? err.message : String(err)}`);
    }
    if (typeof yieldControl === 'function') {
      await yieldControl();
    }
  }

  return anyLoaded;
}

function readMos6502ResetVector(runtime: Unsafe) {
  if (!runtime?.sim || typeof runtime.sim.runner_read_memory !== 'function') {
    return null;
  }
  const loBytes = runtime.sim.runner_read_memory(0xFFFC, 1);
  const hiBytes = runtime.sim.runner_read_memory(0xFFFD, 1);
  const lo = loBytes instanceof Uint8Array && loBytes.length > 0 ? (loBytes[0] & 0xFF) : 0;
  const hi = hiBytes instanceof Uint8Array && hiBytes.length > 0 ? (hiBytes[0] & 0xFF) : 0;
  return ((hi << 8) | lo) & U16_MASK;
}

function readMos6502Byte(runtime: Unsafe, addr: Unsafe) {
  if (!runtime?.sim || typeof runtime.sim.runner_read_memory !== 'function') {
    return 0;
  }
  const bytes = runtime.sim.runner_read_memory(addr & U16_MASK, 1);
  return bytes instanceof Uint8Array && bytes.length > 0 ? (bytes[0] & 0xFF) : 0;
}

function pokeSignalIfPresent(runtime: Unsafe, name: Unsafe, value: Unsafe) {
  if (!runtime?.sim || typeof runtime.sim.has_signal !== 'function' || typeof runtime.sim.poke !== 'function') {
    return;
  }
  if (runtime.sim.has_signal(name)) {
    runtime.sim.poke(name, value);
  }
}

function runBootstrapCycles(runtime: Unsafe, count: Unsafe) {
  const cycles = Math.max(0, Number.parseInt(count, 10) || 0);
  if (cycles <= 0 || !runtime?.sim) {
    return;
  }
  if (typeof runtime.sim.runner_run_cycles === 'function') {
    runtime.sim.runner_run_cycles(cycles, 0, false);
    return;
  }
  if (typeof runtime.sim.run_clock_ticks === 'function' && typeof runtime.sim.has_signal === 'function' && runtime.sim.has_signal('clk')) {
    runtime.sim.run_clock_ticks('clk', cycles);
    return;
  }
  if (typeof runtime.sim.run_ticks === 'function') {
    runtime.sim.run_ticks(cycles);
  }
}

async function bootstrapMos6502Runner(runtime: Unsafe, log: Unsafe = () => {}) {
  if (!runtime?.sim || typeof runtime.sim.runner_kind !== 'function') {
    return;
  }
  if (runtime.sim.runner_kind() !== 'mos6502') {
    return;
  }

  // Match examples/mos6502 IR runner reset/bootstrap flow so CPU starts at reset vector target.
  pokeSignalIfPresent(runtime, 'rst', 1);
  pokeSignalIfPresent(runtime, 'rdy', 1);
  pokeSignalIfPresent(runtime, 'irq', 1);
  pokeSignalIfPresent(runtime, 'nmi', 1);
  pokeSignalIfPresent(runtime, 'data_in', 0);
  pokeSignalIfPresent(runtime, 'ext_pc_load_en', 0);
  pokeSignalIfPresent(runtime, 'ext_a_load_en', 0);
  pokeSignalIfPresent(runtime, 'ext_x_load_en', 0);
  pokeSignalIfPresent(runtime, 'ext_y_load_en', 0);
  pokeSignalIfPresent(runtime, 'ext_sp_load_en', 0);

  runBootstrapCycles(runtime, 1); // one reset pulse cycle
  pokeSignalIfPresent(runtime, 'rst', 0);
  runBootstrapCycles(runtime, 5); // settle control reset sequence

  const targetAddr = readMos6502ResetVector(runtime);
  if (targetAddr == null) {
    log('MOS6502 bootstrap skipped: reset vector unavailable');
    return;
  }
  const opcode = readMos6502Byte(runtime, targetAddr);
  pokeSignalIfPresent(runtime, 'ext_pc_load_data', targetAddr);
  pokeSignalIfPresent(runtime, 'ext_pc_load_en', 1);
  pokeSignalIfPresent(runtime, 'data_in', opcode);

  if (typeof runtime.sim.has_signal === 'function'
    && runtime.sim.has_signal('clk')
    && typeof runtime.sim.evaluate === 'function'
    && typeof runtime.sim.tick === 'function'
    && typeof runtime.sim.poke === 'function') {
    runtime.sim.poke('clk', 0);
    runtime.sim.evaluate();
    runtime.sim.poke('clk', 1);
    runtime.sim.tick();
  } else {
    runBootstrapCycles(runtime, 1);
  }

  pokeSignalIfPresent(runtime, 'ext_pc_load_en', 0);
  log(`MOS6502 bootstrap complete (PC target $${targetAddr.toString(16).toUpperCase().padStart(4, '0')})`);
}

export function resolveInitializationContext({
  options = {} as Unsafe,
  dom,
  state,
  getRunnerPreset,
  parseIrMeta,
  log = (() => {}) as Unsafe
}: Unsafe = {}) {
  const simJson = String(options.simJson ?? dom.irJson?.value ?? '').trim();
  if (!simJson) {
    return null;
  }

  if (dom.irJson && simJson !== String(dom.irJson.value || '').trim()) {
    dom.irJson.value = simJson;
  }

  const preset = options.preset || getRunnerPreset(dom.runnerSelect?.value || state.runnerPreset);
  const simMeta = parseIrMeta(simJson);

  let explorerSource = String(options.explorerSource ?? simJson);
  let explorerMeta = options.explorerMeta || null;
  if (!explorerMeta && explorerSource && explorerSource !== simJson) {
    try {
      explorerMeta = parseIrMeta(explorerSource);
    } catch (err) {
      log(`Explorer IR parse failed, using simulation IR: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  if (!explorerMeta) {
    explorerMeta = simMeta;
    explorerSource = simJson;
  }
  if (explorerMeta !== simMeta) {
    explorerMeta = { ...explorerMeta, liveSignalNames: simMeta.names };
  }

  return {
    simJson,
    preset,
    simMeta,
    explorerSource,
    explorerMeta
  };
}

export function resetSimulatorSession({
  runtime,
  state,
  appStore,
  storeActions,
  createSimulator,
  backend,
  simJson,
  simMeta,
  setCycleState,
  setUiCyclesPendingState,
  setRunningState,
  updateApple2SpeakerAudio,
  setMemoryDumpStatus,
  setMemoryResetVectorInput
}: Unsafe = {}) {
  if (runtime.sim) {
    runtime.sim.destroy();
  }

  runtime.sim = createSimulator(runtime.instance, simJson, backend);
  runtime.irMeta = simMeta;
  setCycleState(0);
  setUiCyclesPendingState(0);
  setRunningState(false);
  appStore.dispatch(storeActions.watchClear());
  appStore.dispatch(storeActions.breakpointClear());

  state.apple2.enabled = false;
  state.apple2.keyQueue = [];
  state.apple2.lastCpuResult = null;
  state.apple2.lastSpeakerToggles = 0;
  state.apple2.lastMappedSoundValue = null;
  state.apple2.baseRomBytes = null;
  state.apple2.ioConfig = null;
  state.apple2.sourceMap = null;
  if (runtime.throughput && typeof runtime.throughput === 'object') {
    runtime.throughput.cyclesPerSecond = 0;
    runtime.throughput.lastSampleTimeMs = null;
    runtime.throughput.lastSampleCycle = 0;
  }

  updateApple2SpeakerAudio(0, 0);
  setMemoryDumpStatus('');
  setMemoryResetVectorInput(null);
}

export function seedDefaultWatchSignals({
  runtime,
  simMeta,
  addWatchSignal,
  selectedClock
}: Unsafe = {}) {
  const outputs = runtime.sim.output_names();
  for (const name of outputs.slice(0, 4)) {
    addWatchSignal(name);
  }

  const clk = selectedClock();
  if (clk) {
    addWatchSignal(clk);
  } else if (simMeta.clocks.length > 0) {
    addWatchSignal(simMeta.clocks[0]);
  }
}

async function loadDefaultSrcmapAsset({
  state,
  preset,
  fetchImpl,
  log
}: Unsafe = {}) {
  const raw = preset?.defaultSrcmap;
  if (!raw || typeof raw !== 'object') {
    return;
  }
  const path = String(raw.path || '').trim();
  if (!path) {
    return;
  }

  try {
    const resp = await fetchImpl(path);
    if (!resp.ok) {
      log(`Source map load skipped (${resp.status}): ${path}`);
      return;
    }
    const json = await resp.json();
    const srcmap = createRiscvSourceMap(json);
    if (srcmap) {
      state.apple2.sourceMap = srcmap;
      log(`Loaded source map: ${path} (${srcmap.functionCount} functions, ${srcmap.lineCount} lines)`);
    } else {
      log(`Source map format not recognized: ${path}`);
    }
  } catch (err) {
    log(`Failed to load source map: ${err instanceof Error ? err.message : String(err)}`);
  }
}

export async function initializeApple2Mode({
  runtime,
  state,
  preset,
  addWatchSignal,
  transferChunkBytes = DEFAULT_TRANSFER_CHUNK_BYTES,
  yieldControl = null,
  fetchImpl = globalThis.fetch,
  log = () => {}
}: Unsafe = {}) {
  const ioConfig = resolveRunnerIoConfig(preset);
  const defaultAssets = resolveDefaultAssetsConfig(preset);
  const defaultDisk = resolveDefaultDiskConfig(preset);
  const defaultBin = resolveDefaultBinConfig(preset);
  state.apple2.ioConfig = ioConfig;

  const supportsRunnerApi = supportsRunnerApiForRuntime(runtime);
  const supportsGenericMemoryApi = typeof runtime.sim?.memory_mode === 'function'
    && runtime.sim.memory_mode() != null;
  const wantsMemoryApi = ioConfig.enabled || !!ioConfig.rom?.path || defaultAssets.length > 0 || !!defaultDisk || !!defaultBin;

  if (!wantsMemoryApi) {
    return;
  }

  if (!supportsRunnerApi && !supportsGenericMemoryApi) {
    log(`Runner ${preset?.id || 'unknown'} requested memory-mapped features but has no supported memory API.`);
    return;
  }

  if (ioConfig.enabled) {
    state.apple2.enabled = true;
    const watchSignals = Array.isArray(ioConfig.watchSignals) && ioConfig.watchSignals.length > 0
      ? ioConfig.watchSignals
      : DEFAULT_APPLE2_WATCHES;
    for (const name of watchSignals) {
      if (runtime.sim.has_signal(name)) {
        addWatchSignal(name);
      }
    }
  }

  await loadRunnerRomAsset({
    runtime,
    state,
    ioConfig,
    supportsRunnerApi,
    supportsGenericMemoryApi,
    transferChunkBytes,
    yieldControl,
    fetchImpl,
    log
  });
  if (typeof yieldControl === 'function') {
    await yieldControl();
  }

  if (defaultAssets.length > 0) {
    await loadDefaultAssets({
      runtime,
      defaultAssets,
      supportsRunnerApi,
      supportsGenericMemoryApi,
      transferChunkBytes,
      yieldControl,
      fetchImpl,
      log
    });
  } else {
    await loadDefaultDiskAsset({
      runtime,
      defaultDisk,
      supportsRunnerApi,
      transferChunkBytes,
      yieldControl,
      fetchImpl,
      log
    });
    if (typeof yieldControl === 'function') {
      await yieldControl();
    }

    await loadDefaultBinAsset({
      runtime,
      defaultBin,
      supportsRunnerApi,
      supportsGenericMemoryApi,
      transferChunkBytes,
      yieldControl,
      fetchImpl,
      log
    });
  }

  await loadDefaultSrcmapAsset({ state, preset, fetchImpl, log });
}
